/**
 * Domain types for the Module Library, mirroring the backend DTOs in
 * `hub-plus-api` under
 * `App\Module\Hub\Feature\LearningCenter\DTO\{Request,Response}` for the
 * `ModuleLibrary` controllers.
 *
 * The backend response envelope is always `{ data, meta }`.
 */

import { CourseContentBlockType } from "@/modules/learningCenter/features/CourseManagement/types/course";

/** Mirrors `LibraryModuleStatus` enum. */
export type LibraryModuleStatus = "draft" | "published" | "archived";

/**
 * Module difficulty options. This is a distinct taxonomy from course *levels*
 * (Foundational/Intermediate/Advanced/Expert in CourseDirectory/utils/level.ts)
 * — modules use beginner/intermediate/advanced. Keep the two separate.
 */
export const LIBRARY_MODULE_DIFFICULTY_OPTIONS = [
  "beginner",
  "intermediate",
  "advanced",
] as const;

/** Mirrors `LibraryModuleContentBlockResponse`. */
export interface LibraryModuleContentBlock {
  uuid: string;
  type: CourseContentBlockType;
  content: string | null;
  orderNumber: number;
  metadata: Record<string, unknown> | null;
}

/** Mirrors `LibraryModuleResponse` (the full module graph). */
export interface LibraryModule {
  uuid: string;
  title: string;
  description: string | null;
  difficulty: string | null;
  estimatedMinutes: number | null;
  status: LibraryModuleStatus;
  version: number;
  tags: string[];
  blocks: LibraryModuleContentBlock[];
  createdAt: string | null;
  updatedAt: string | null;
}

/** Mirrors `LibraryModuleListItemResponse`. */
export interface LibraryModuleListItem {
  uuid: string;
  title: string;
  description: string | null;
  difficulty: string | null;
  estimatedMinutes: number | null;
  status: LibraryModuleStatus;
  version: number;
  tags: string[];
  blockCount: number;
  updatedAt: string | null;
}

/** Mirrors `LibraryModuleContentBlockRequest`. */
export interface LibraryModuleContentBlockPayload {
  type: CourseContentBlockType;
  content?: string | null;
  orderNumber: number;
  metadata?: Record<string, unknown> | null;
  /** Present when editing an existing block; omitted for new blocks. */
  uuid?: string | null;
}

/** Mirrors `CreateLibraryModuleRequest` / `UpdateLibraryModuleRequest`. */
export interface LibraryModulePayload {
  title: string;
  description?: string | null;
  difficulty?: string | null;
  estimatedMinutes?: number | null;
  tags: string[];
  blocks: LibraryModuleContentBlockPayload[];
}

/** Mirrors `CreateLibraryModuleFromCourseModuleRequest`. */
export interface CreateLibraryModuleFromCourseModulePayload {
  courseModuleUuid: string;
  title?: string | null;
  description?: string | null;
  difficulty?: string | null;
  estimatedMinutes?: number | null;
  tags?: string[];
}
